import React from 'react'
import { User, Shield, Zap, Flame, Award, ExternalLink, Sparkles } from 'lucide-react'
import { tr, useLang } from '../../games/lib/lang.js'

export function ProfileView({ session, bp = 0, rank = null, streak = 0 }) {
  const [lang] = useLang()
  const name = session?.name || session?.email?.split('@')[0] || tr(lang, 'Viajero cósmico', 'Cosmic traveler')

  return (
    <div className="p-4 flex flex-col min-h-[85vh] max-w-md mx-auto space-y-4">
      {/* User Header */}
      <div className="p-5 rounded-2xl bg-gradient-to-b from-[#181329] to-[#0f0c1c] border border-purple-800/40 text-center relative overflow-hidden shadow-xl">
        <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-purple-600 to-indigo-500 mx-auto flex items-center justify-center text-white text-2xl font-black shadow-lg mb-2">
          {name.charAt(0).toUpperCase()}
        </div>
        <h2 className="text-lg font-black text-white">{name}</h2>
        <p className="text-xs text-purple-300 font-semibold mt-0.5">
          {rank ? tr(lang, `Ranking global #${rank}`, `Global ranking #${rank}`) : tr(lang, 'Sin puesto en el ranking global', 'No global rank yet')}
        </p>

        <div className="flex items-center justify-center gap-2 mt-3">
          <span className="px-2.5 py-1 rounded-full bg-slate-900 border border-purple-500/40 text-[10px] font-bold text-purple-300">
            🪐 Unity Cosmos Pro
          </span>
          <span className="px-2.5 py-1 rounded-full bg-emerald-950 border border-emerald-500/40 text-[10px] font-bold text-emerald-400">
            {tr(lang, 'Detox de dopamina activo', 'Dopamine Detox on')}
          </span>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 flex flex-col">
          <span className="text-[11px] text-slate-400 font-medium">{tr(lang, 'Puntuación total', 'Total score')}</span>
          <span className="text-xl font-black text-cyan-400 mt-1">{Number(bp || 0).toLocaleString(lang === 'en' ? 'en-US' : 'es-MX')} pts</span>
          <span className="text-[10px] text-slate-400 mt-0.5">{rank ? tr(lang, `Puesto global #${rank}`, `Global rank #${rank}`) : tr(lang, 'Aún fuera de la tabla', 'Not on the table yet')}</span>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 flex flex-col">
          <span className="text-[11px] text-slate-400 font-medium">{tr(lang, 'Racha', 'Streak')}</span>
          <span className="text-xl font-black text-emerald-400 mt-1">{tr(lang, `${streak} días`, `${streak} days`)}</span>
          <span className="text-[10px] text-slate-400 mt-0.5">{tr(lang, 'Días seguidos entrenando', 'Days trained in a row')}</span>
        </div>
      </div>

      {/* Badges Earned */}
      <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 space-y-3">
        <h3 className="text-xs font-bold text-white flex items-center gap-2">
          <Award size={16} className="text-yellow-400" />
          <span>{tr(lang, 'Insignias de la constelación', 'Constellation badges')}</span>
        </h3>

        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="p-2 rounded-xl bg-slate-950 border border-slate-800 flex flex-col items-center">
            <span className="text-2xl mb-1">🔥</span>
            <span className="text-[10px] font-bold text-white leading-tight">{tr(lang, 'Fuego puro', 'Pure fire')}</span>
            <span className="text-[9px] text-slate-400 mt-0.5">{tr(lang, 'Racha de días', 'Day streak')}</span>
          </div>

          <div className="p-2 rounded-xl bg-slate-950 border border-slate-800 flex flex-col items-center">
            <span className="text-2xl mb-1">🛡️</span>
            <span className="text-[10px] font-bold text-white leading-tight">Anti-Doom</span>
            <span className="text-[9px] text-slate-400 mt-0.5">{tr(lang, '30 min de foco', '30 min of focus')}</span>
          </div>

          <div className="p-2 rounded-xl bg-slate-950 border border-slate-800 flex flex-col items-center">
            <span className="text-2xl mb-1">⚡</span>
            <span className="text-[10px] font-bold text-white leading-tight">{tr(lang, 'Sinapsis Blitz', 'Synapse Blitz')}</span>
            <span className="text-[9px] text-slate-400 mt-0.5">Top 1v1</span>
          </div>
        </div>
      </div>

      {/* Unity Cosmos Link */}
      <div className="p-4 rounded-2xl bg-gradient-to-r from-blue-950/60 to-slate-900 border border-blue-800/40 flex items-center justify-between">
        <div>
          <h4 className="text-xs font-bold text-white">{tr(lang, 'Hub de Unity Cosmos', 'Unity Cosmos hub')}</h4>
          <p className="text-[10px] text-slate-400">{tr(lang, 'Una sola cuenta para toda la constelación', 'One account for the whole constellation')}</p>
        </div>
        <a
          href="https://unitycosmos.com"
          target="_blank"
          rel="noreferrer"
          className="p-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white transition-colors"
        >
          <ExternalLink size={16} />
        </a>
      </div>
    </div>
  )
}
